/**
 * Margen de espera de la ubicación al enviar la evidencia (QA-3 de la 005). **Funciones puras**,
 * sin timers: `use-marking.ts` las consulta justo antes de enviar.
 *
 * Con la evidencia VPP lista y la lectura de ubicación todavía en curso, se la espera un poco
 * (`LOCATION_GRACE_MS`), pero **nunca** tanto como para acercar el envío al borde de la ventana
 * con que el servidor acepta la evidencia. Por eso la gracia efectiva es
 *
 *     gracia = min(LOCATION_GRACE_MS, max(0, presupuesto − transcurrido))
 *
 * y cada modo tiene su presupuesto, pensado para dejar **≥ 1,5 s** (`SUBMIT_SAFETY_MARGIN_MS`) de
 * holgura a la ventana del servidor para la red y el reloj. La ubicación nunca empuja el envío más
 * allá del presupuesto: si la evidencia llegó tarde, la gracia es 0 y se envía de inmediato.
 *
 * **Modo normal** (`POST /challenges/{id}/verify`, `apps/api/src/modules/vpp/evidence.ts` paso 5):
 * el servidor acepta si
 * `recepción ≤ display_started_at + frames × frame_interval_ms + VPP_VERIFY_WINDOW_MARGIN_MS`
 * (1200 + 1500 = 2,7 s con los defectos). El TTL de 8 s no es lo que limita: esta ventana es más
 * corta. El margen del servidor **no viaja al cliente**, así que se asume su defecto (1500 ms,
 * `ASSUMED_VERIFY_WINDOW_MARGIN_MS`). Presupuesto = `frames × frame_interval_ms + 1500 − 1500` =
 * la duración de la secuencia, contado desde el inicio **estimado** de la emisión (el frame `i` se
 * pinta en `inicio + (i − 1) × frame_interval_ms`). Con los defectos, leyendo 4 frames en ~0,6 s,
 * la gracia real es de ~0,6 s, no de 1,5 s. Si una empresa bajara `VPP_VERIFY_WINDOW_MARGIN_MS`
 * a su mínimo (500), la holgura sería de 0,5 s: la gracia sigue sin pasarse de la secuencia.
 *
 * **Modo autónomo** (`POST /marks/autonomous`, `modules/vpp/autonomous.ts#isSlotWithinWindow`): cada
 * slot debe empezar dentro de
 * `[recepción − AUTONOMOUS_CLOCK_SKEW_MS − AUTONOMOUS_VERIFY_MARGIN_MS, recepción + skew]`, es
 * decir, hasta 5 s hacia atrás con los defectos (3000 + 2000). Presupuesto =
 * `AUTONOMOUS_SUBMIT_BUDGET_MS` = 2,5 s desde el **primer frame capturado**, el mismo valor que el
 * tope de evidencia `AUTONOMOUS_EVIDENCE_DEADLINE_MS`: así la espera por ubicación **nunca crea un
 * peor caso nuevo**, el envío sale como máximo cuando ya salía antes de QA-3. Cuenta: el slot más
 * viejo empieza a lo sumo un slot antes de su captura (≤ 400 ms, tope de `AUTONOMOUS_SLOT_MS`),
 * así que al enviar su antigüedad es ≤ 2,5 + 0,4 = 2,9 s y quedan ≥ 2,1 s (≥ 1,5 s) para la red y
 * un reloj del marcador algo atrasado.
 */
import type { ChallengeRef } from '@fermin/contracts';

import { AUTONOMOUS_EVIDENCE_DEADLINE_MS } from '../../camera';

import type { FrameCollection } from './frames';

/** Tope de la espera por ubicación con la evidencia lista (si cabe en el presupuesto). */
export const LOCATION_GRACE_MS = 1_500;

/** Holgura mínima que el presupuesto deja a la ventana del servidor (red, reloj). */
export const SUBMIT_SAFETY_MARGIN_MS = 1_500;

/** Defecto de `VPP_VERIFY_WINDOW_MARGIN_MS` en la API; el valor real no viaja al cliente. */
export const ASSUMED_VERIFY_WINDOW_MARGIN_MS = 1_500;

/** Presupuesto del modo autónomo desde el primer frame capturado (ver cabecera). */
export const AUTONOMOUS_SUBMIT_BUDGET_MS = AUTONOMOUS_EVIDENCE_DEADLINE_MS;

/** `min(LOCATION_GRACE_MS, max(0, presupuesto − transcurrido))`. */
export function effectiveLocationGraceMs(budgetMs: number, elapsedMs: number): number {
  return Math.min(LOCATION_GRACE_MS, Math.max(0, budgetMs - elapsedMs));
}

/** Presupuesto del modo normal, desde el inicio estimado de la emisión. */
export function normalSubmitBudgetMs(
  challenge: Pick<ChallengeRef, 'frames_count' | 'frame_interval_ms'>,
): number {
  return Math.max(
    0,
    challenge.frames_count * challenge.frame_interval_ms +
      ASSUMED_VERIFY_WINDOW_MARGIN_MS -
      SUBMIT_SAFETY_MARGIN_MS,
  );
}

/**
 * Inicio estimado de la emisión del challenge, en el reloj monotónico de la captura: el frame `i`
 * se pinta en `inicio + (i − 1) × intervalo`, así que cada frame leído da una cota superior y se
 * toma la menor. `null` sin frames.
 */
export function estimatedEmissionStartMs(
  collection: FrameCollection,
  frameIntervalMs: number,
): number | null {
  if (collection.firstAtMs === null || collection.frames.length === 0) {
    return null;
  }
  let start = Number.POSITIVE_INFINITY;
  for (const frame of collection.frames) {
    start = Math.min(start, collection.firstAtMs + frame.dtMs - (frame.i - 1) * frameIntervalMs);
  }
  return start;
}

/** Gracia efectiva del modo normal al enviar en `nowMs`. */
export function normalLocationGraceMs(
  challenge: Pick<ChallengeRef, 'frames_count' | 'frame_interval_ms'>,
  collection: FrameCollection,
  nowMs: number,
): number {
  const start = estimatedEmissionStartMs(collection, challenge.frame_interval_ms);
  if (start === null) {
    return 0;
  }
  return effectiveLocationGraceMs(normalSubmitBudgetMs(challenge), nowMs - start);
}

/** Gracia efectiva del modo autónomo al enviar en `nowMs` (desde el primer frame capturado). */
export function autonomousLocationGraceMs(firstAtMs: number | null, nowMs: number): number {
  if (firstAtMs === null) {
    return 0;
  }
  return effectiveLocationGraceMs(AUTONOMOUS_SUBMIT_BUDGET_MS, nowMs - firstAtMs);
}
